import {
  Alert,
  Box,
  Button,
  FormControl,
  FormHelperText,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useIntelAccess } from '@pn/core/permissions/isIntelAccessDenied';
import { CustomSearchInput } from '@pn/ui/custom-components/CustomSearchInput';
import { isEmpty, isNil } from 'lodash-es';
import React from 'react';
import { useWellLicenseeHistory } from 'src/application/loaders/tools/useWellLicenseeHistory';
import { InternalLink } from 'src/ui/components/InternalLink';
import { UpgradeAccountToAccessMessage } from 'src/ui/components/UpgradeAccountToAccessMessage';
import { companiesURL } from 'src/ui/pages';

const MAX_LENGTH = 26;

export function LicenseeHistoryLookup() {
  const [wellLicenseOrUWI, setWellLicenseOrUWI] = React.useState('');
  const { history, errorMsg, isFetching, fetch, clear } =
    useWellLicenseeHistory();

  const access = useIntelAccess();
  const isAccessDenied = access('wellLicenseHistory').denied();

  const handleSubmit = async () => {
    if (
      isEmpty(wellLicenseOrUWI) ||
      isAccessDenied ||
      wellLicenseOrUWI.length > MAX_LENGTH ||
      isFetching
    )
      return;
    await fetch(wellLicenseOrUWI);
  };

  const handleClear = () => {
    setWellLicenseOrUWI('');
    clear();
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: '1fr min-content',
          alignItems: 'start',
          mb: 2,
        }}
      >
        <FormControl>
          <CustomSearchInput
            value={wellLicenseOrUWI}
            onChange={setWellLicenseOrUWI}
            placeholder="UWI or Licence Number"
            margin="normal"
            disabled={isFetching || isAccessDenied}
            size="small"
            onEnter={handleSubmit}
            onClear={handleClear}
            sx={{ my: 0 }}
          />
          <FormHelperText id="outlined-land-id-helper-text">
            UWI or Licence Number
          </FormHelperText>
        </FormControl>

        <Button
          type="submit"
          variant="contained"
          loading={isFetching}
          onClick={handleSubmit}
          disabled={
            isFetching ||
            isEmpty(wellLicenseOrUWI) ||
            isAccessDenied ||
            wellLicenseOrUWI.length > MAX_LENGTH
          }
        >
          Submit
        </Button>
      </Box>
      {isFetching ? (
        <LinearProgress />
      ) : (
        <>
          {errorMsg && <Alert severity="warning">{errorMsg}</Alert>}
          {history != null && history.length > 0 && (
            <>
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  {history.length} licensee{history.length > 1 ? 's' : ''}{' '}
                  found:
                </Typography>

                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Licensee</TableCell>
                        <TableCell>Start Date</TableCell>
                        <TableCell>End Date</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {history.map((record, index) => (
                        <TableRow
                          key={index}
                          sx={{
                            '&:last-child td, &:last-child th': { border: 0 },
                          }}
                        >
                          <TableCell>
                            {isNil(record.company_brand_id) ? (
                              record.licensee
                            ) : (
                              <InternalLink
                                to={`${companiesURL}/${record.company_brand_id}`}
                              >
                                {record.licensee}
                              </InternalLink>
                            )}
                          </TableCell>
                          <TableCell>{record.start_date}</TableCell>
                          <TableCell>{record.end_date ?? '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </>
          )}
        </>
      )}

      {isAccessDenied && <UpgradeAccountToAccessMessage />}
    </Box>
  );
}
